import { confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import FormText from 'components/form/FormText';
import IndicatorButton from 'components/indicator/IndicatorButton';
import { AppConst } from 'constants/appConst';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 *　ログイン認証コード入力画面
 */
export default function SigninAuth(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    confirmationCode: string;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const { postCompanyRegister } = useApiState();
  const navigate = useNavigate();
  const location = useLocation();

  const [processing, setProcessing] = useState(false);
  const { initializeAcctState } = useAppState();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // パラメタの検証
    if (AppHelper.isEmpty(location.state?.mailAddress ?? null)) {
      navigate(-1);
    }
    if (AppHelper.isEmpty(location.state?.nextTo ?? null)) {
      navigate(-1);
    }
  }, [location.state, navigate]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　確認ボタン押下時の処理
   */
  const onConfirm = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // 認証コードの検証
          const response = await confirmSignIn({
            challengeResponse: data.confirmationCode,
          });

          if (!response.isSignedIn) {
            setError('confirmationCode', {
              type: 'custom',
              message: '認証コードが間違っています。',
            });
            return;
          }

          // サインアップの場合は企業情報登録
          if (location.state?.signupData) {
            await _signupCompany(location.state.signupData);
          }

          // ステート情報の更新と画面遷移
          await initializeAcctState();
          navigate(location.state?.nextTo);
        } catch (e) {
          // 認証コードの失効、または間違い上限回数に達した場合
          if (e.name === 'NotAuthorizedException') {
            setError('confirmationCode', {
              type: 'custom',
              message:
                'セッションが失効しました。前の画面に戻って認証コードの発行からやり直してください。',
            });
          } else {
            showBoundary(e);
          }
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 企業情報登録
   */
  const _signupCompany = async (signupData: any) => {
    try {
      // Cognito認証情報の取得
      const session = await fetchAuthSession();

      // カンパニー登録APIの呼び出し
      await postCompanyRegister({
        company: {
          name: signupData.companyName,
          tel: signupData.tel,
          zipCode: signupData.zipCode,
          prefectureDiv: signupData.prefectureDiv,
          city: signupData.city,
          address: signupData.address,
          buildingName: signupData.buildingName,
          imageUploadKey: null,
          reportMailSenderName: signupData.companyName,
          reportMailReplyAddress: 'no-reply@branq.jp',
          reportMailSignatureHtml: _generateSignature(signupData),
          privacyPolicyUrl: null,
          copyright: null,
          termsInputDiv: DivConst.items.termsInputType.url.value,
          termsUrl: null,
          termsText: null,
          apiKeyBenchmark: null,
          apiKeyGoogleAnalytics: null,
        },
        user: {
          accessToken: session.tokens.accessToken.toString(),
          name: signupData.fullName,
          loginRouteDiv: DivConst.items.loginRoute.mail.value,
        },
      });
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 署名を生成
   */
  const _generateSignature = (signupData: any): string => {
    const zipCode = signupData.zipCode.replace(/(\d{3})(\d{4})/, '$1-$2');
    const prefecture =
      signupData.prefectureDiv &&
      parseInt(signupData.prefectureDiv) !== DivConst.items.prefecture.abroad.value
        ? AppHelper.getDivLabel('prefecture', parseInt(signupData.prefectureDiv))
        : '';
    const address = [prefecture, signupData.city, signupData.address, signupData.buildingName]
      .filter(Boolean)
      .join('');

    return `
      <p>ーーーーーーーーーーーーーー</p>
      <p>${signupData.companyName}</p>
      <p>〒${zipCode}</p>
      <p>${address}</p>
    `;
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-login">
        <div className="container">
          <div className="card card-login">
            <div className="card-body">
              <h1 className="card-title">認証コードを送信しました</h1>
              <p className="lead">
                {location.state?.mailAddress}
                に認証コードを送信しました。メールに記載されたコードを入力してください。
              </p>

              <form className="form-login">
                <div>
                  <FormText
                    subject="認証コード"
                    name="confirmationCode"
                    register={register}
                    errors={errors}
                    required={true}
                    format="numeric"
                    minLength={6}
                    maxLength={6}
                    requiredMark={true}
                  />
                </div>
                <div className="form-footer">
                  <button
                    type="button"
                    className="btn btn-submit w-100"
                    onClick={onConfirm}
                    disabled={processing}
                  >
                    <IndicatorButton visible={processing} />
                    コードの確認
                  </button>
                  <p className="mt-3">
                    <label className="form-label">
                      <a onClick={() => navigate(-1)}>前の画面に戻る</a>
                    </label>
                  </p>
                </div>
              </form>
            </div>
          </div>
          <div className="text-center">
            <a href={AppConst.url.privacy} target="_blank" className="caption">
              privacy &amp; Terms
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
